import { LoadingButton } from "@mui/lab";
import { Box, TextField, Typography } from "@mui/material";
import { forwardRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setNotification } from "../../store/notificationSlice";
import useContactValidation, { IProps } from "../Validation/contact";
import style from "./style.module.scss";
import Api from "../../openapi";
import { getTenant } from "../../theme";
import { DropzoneArea } from "mui-file-dropzone";
import { CrossIcon } from "../../assets/icons";
import { useMediaQuery } from "@react-hook/media-query";

interface IContact {
  onClose: Function;
}

export const Contact = forwardRef((props: IContact, ref) => {
  const { t, i18n } = useTranslation("contactPartner");
  const isRTL = i18n.language === "ar";
  const mobile = useMediaQuery("only screen and (max-width: 600px)");
  const defaultInfo: IProps = {
    email: "",
    message: "",
    reason: "",
  };
  const [contactInfo, setContactInfo] = useState<IProps>(defaultInfo);
  const { validation, setValidation } = useContactValidation(contactInfo);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const maxFileSize = 5242880;
  const [attachment, setAttachment] = useState<any>();

  const handleFieldChange = (field: any, value?: any) => {
    setContactInfo((state: any) => ({ ...state, [field]: value }));
  };

  const fileChange = (files: any) => {
    setAttachment(files);
  };

  const sendRequest = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }
    try {
      setLoading(true);
      const { email, message, reason } = contactInfo;

      await Api.Contact.contactPartner(
        {
          message: String(message),
          email: String(email),
          reason: String(reason),
        },
        attachment?.[0]
      );

      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: t("notification.success"),
        })
      );
      setContactInfo(defaultInfo);
      setValidation((state) => ({ ...state, show: false }));
      props.onClose();
    } catch (error) {}
    setLoading(false);
  };

  const handleClose = () => {
    props.onClose();
  };

  return (
    <Box ref={ref} className={style.section6}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <Typography
          sx={{
            maxWidth: "728px",
            color: "#191E2A",
            fontSize: ["18px", "24px"],
            fontWeight: 600,
            lineHeight: "24px",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          {t("title")}
        </Typography>
        <CrossIcon onClick={handleClose} />
      </Box>

      <Box
        component="form"
        noValidate
        sx={{
          width: ["100%"],
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          "& .MuiDropzone": { minHeight: "auto !important", marginBottom: "10px" },
        }}
      >
        <TextField
          placeholder={t("form.email.placeholder")}
          label={t("form.email.label")}
          className={style.text_input}
          value={contactInfo.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          error={validation.show && validation.email?.error}
          helperText={validation.show && validation.email?.error && validation.email.helperText}
          sx={{
            "& .MuiOutlinedInput-notchedOutline legend": {
              textAlign: isRTL ? "right" : "left",
              marginLeft: isRTL ? "auto" : 0,
              marginRight: isRTL ? 0 : "auto",
            },
          }}
          inputProps={{
            style: { textAlign: isRTL ? "right" : "left", direction: isRTL ? "rtl" : "ltr" },
          }}
          InputLabelProps={{
            sx: {
              transformOrigin: isRTL ? "top right" : "top left",
              left: isRTL ? "auto" : undefined,
              right: isRTL ? "1.75rem" : "auto",
            },
          }}
          FormHelperTextProps={{
            sx: {
              textAlign: isRTL ? "right" : "left",
              marginRight: isRTL ? "14px" : "14px",
              marginLeft: 0,
            },
          }}
        />

        <TextField
          placeholder={t("form.reason.placeholder")}
          label={t("form.reason.label")}
          className={style.text_input}
          value={contactInfo.reason}
          onChange={(e) => handleFieldChange("reason", e.target.value)}
          error={validation.show && validation.reason?.error}
          helperText={validation.show && validation.reason?.error && validation.reason.helperText}
          sx={{
            "& .MuiOutlinedInput-notchedOutline legend": {
              textAlign: isRTL ? "right" : "left",
              marginLeft: isRTL ? "auto" : 0,
              marginRight: isRTL ? 0 : "auto",
            },
          }}
          inputProps={{
            style: { textAlign: isRTL ? "right" : "left", direction: isRTL ? "rtl" : "ltr" },
          }}
          InputLabelProps={{
            sx: {
              transformOrigin: isRTL ? "top right" : "top left",
              left: isRTL ? "auto" : undefined,
              right: isRTL ? "1.75rem" : "auto",
            },
          }}
          FormHelperTextProps={{
            sx: {
              textAlign: isRTL ? "right" : "left",
              marginRight: isRTL ? "14px" : "14px",
              marginLeft: 0,
            },
          }}
        />

        <TextField
          sx={{
            "& .MuiInputBase-root": { height: "auto", minHeight: "102px" },
            "& .MuiOutlinedInput-notchedOutline legend": {
              textAlign: isRTL ? "right" : "left",
              marginLeft: isRTL ? "auto" : 0,
              marginRight: isRTL ? 0 : "auto",
            },
          }}
          className={style.text_input}
          label={t("form.message.label")}
          placeholder={t("form.message.placeholder")}
          value={contactInfo.message}
          onChange={(e) => handleFieldChange("message", e.target.value)}
          error={validation.show && validation.message?.error}
          helperText={validation.show && validation.message?.error && validation.message.helperText}
          multiline
          inputProps={{
            style: { textAlign: isRTL ? "right" : "left", direction: isRTL ? "rtl" : "ltr" },
          }}
          InputLabelProps={{
            sx: {
              transformOrigin: isRTL ? "top right" : "top left",
              left: isRTL ? "auto" : undefined,
              right: isRTL ? "1.75rem" : "auto",
            },
          }}
          FormHelperTextProps={{
            sx: {
              textAlign: isRTL ? "right" : "left",
              marginRight: isRTL ? "14px" : "14px",
              marginLeft: 0,
            },
          }}
        />

        {mobile ? null : (
          <DropzoneArea
            dropzoneClass="MuiDropzone"
            dropzoneText={t("form.attachment")}
            maxFileSize={maxFileSize}
            fileObjects={attachment}
            filesLimit={1}
            acceptedFiles={["image/*", "application/pdf"]}
            onChange={fileChange}
          />
        )}

        <LoadingButton
          fullWidth
          color="primary"
          loading={loading}
          variant="contained"
          disableElevation
          onClick={sendRequest}
        >
          {loading ? <span>&nbsp;</span> : <span>{t("form.send")}</span>}
        </LoadingButton>
      </Box>
    </Box>
  );
});
